import React, { CSSProperties, FormEvent, ReactNode } from "react";
import { Modal, Box, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(() => ({
  box: {
    position: "absolute",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    borderRadius: "10px",
    outline: "none",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  boxAuto: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "85vh",
  },
  // Фиксированные отступы сверху/снизу в целых px: полпикселя от translateY заставляют
  // линию под шапкой мерцать при скролле, здесь по вертикали трансформа нет
  boxInset: {
    left: "50%",
    transform: "translateX(-50%)",
  },
  header: {
    flexShrink: 0,
    padding: "20px 50px 14px 30px",
    // inset-тень вместо border: рисуется внутри шапки и не попадает в область
    // перерисовки скроллящегося контента
    boxShadow: "inset 0 -1px 0 #E1E6F1",
    position: "relative",
    zIndex: 1,
  },
  closeBtn: {
    position: "absolute",
    top: "15px",
    right: "15px",
    cursor: "pointer",
    color: "#54606F",
  },
  titleMain: {
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "24px",
    color: "#1A1E24",
  },
  titleSub: {
    marginTop: "4px",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#A1ADBA",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  body: {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    minHeight: 0,
  },
  sidebar: {
    flexShrink: 0,
    overflowY: "auto",
    background: "#F3F5F7",
    borderRight: "1px solid #E1E6F1",
  },
  main: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  content: {
    flex: 1,
    overflowY: "scroll",
    // right=20 (а не 30): 10px съедает вертикальный скроллбар, итоговый правый край
    // инпутов совпадает с краем кнопок в footer (30/30)
    padding: "12px 20px 0 30px",
    "&::-webkit-scrollbar": {
      width: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#D0D7E2",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#A1ADBA",
    },
  },
  footer: {
    flexShrink: 0,
    padding: "16px 30px",
    boxShadow: "inset 0 1px 0 #E1E6F1",
    position: "relative",
    zIndex: 1,
  },
}));

type ModalLayoutProps = {
  open: boolean;
  onClose: () => void;
  /** Ключ i18n или обычная строка */
  title: string;
  /** Вторая строка под заголовком */
  subtitle?: ReactNode;
  /** Кнопки/actions снизу */
  footer?: ReactNode;
  /** Если передан, контент+footer оборачиваются в <form> */
  onSubmit?: (event: FormEvent) => void;
  /**
   * Колонка слева от контента, на всю высоту. Рендерится вне <form>,
   * чтобы её кнопки не сабмитили форму.
   */
  sidebar?: ReactNode;
  /** Ширина модалки, по умолчанию 442px */
  maxWidth?: string;
  /**
   * Отступ сверху и снизу в px (например "60px"). Задан — модалка тянется на всю
   * доступную высоту; не задан — высота по контенту, но не больше 85vh.
   */
  verticalInset?: string;
  /** Контент, который скроллится */
  children?: ReactNode;
};

/** ModalLayout — обёртка модалки со sticky заголовком и footer'ом. */
const ModalLayout = ({
  open,
  onClose,
  title,
  subtitle,
  footer,
  onSubmit,
  sidebar,
  maxWidth = "442px",
  verticalInset,
  children,
}: ModalLayoutProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const boxStyle: CSSProperties = { maxWidth };
  if (verticalInset) {
    boxStyle.top = verticalInset;
    boxStyle.bottom = verticalInset;
  }

  const body = (
    <>
      <div className={classes.content}>{children}</div>
      {footer && <div className={classes.footer}>{footer}</div>}
    </>
  );

  const main = onSubmit ? (
    <form onSubmit={onSubmit} className={classes.main}>
      {body}
    </form>
  ) : (
    <div className={classes.main}>{body}</div>
  );

  return (
    <Modal open={open} onClose={onClose} BackdropProps={{ className: "MuiBackDropClass" }}>
      <Box
        className={`${classes.box} ${verticalInset ? classes.boxInset : classes.boxAuto}`}
        style={boxStyle}
      >
        <div className={classes.header}>
          <CloseIcon className={classes.closeBtn} onClick={onClose} />
          <div className={classes.titleMain}>{t(title, title)}</div>
          {subtitle && <div className={classes.titleSub}>{subtitle}</div>}
        </div>

        {sidebar ? (
          <div className={classes.body}>
            <div className={classes.sidebar}>{sidebar}</div>
            {main}
          </div>
        ) : (
          main
        )}
      </Box>
    </Modal>
  );
};

export default ModalLayout;
